import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, X, Check, CheckCheck, Trash2, AlertTriangle, 
  Info, AlertCircle, ChevronRight, Clock, Archive,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_CONFIG = {
  critical: { color: 'bg-red-500', textColor: 'text-red-600', bgLight: 'bg-red-50 border-red-200', icon: AlertTriangle, label: 'Crítica' },
  high: { color: 'bg-orange-500', textColor: 'text-orange-600', bgLight: 'bg-orange-50 border-orange-200', icon: AlertCircle, label: 'Alta' },
  medium: { color: 'bg-yellow-500', textColor: 'text-yellow-600', bgLight: 'bg-yellow-50 border-yellow-200', icon: Info, label: 'Media' },
  low: { color: 'bg-gray-400', textColor: 'text-gray-500', bgLight: 'bg-gray-50 border-gray-200', icon: Info, label: 'Baja' }
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'ahora';
  if (diffMins < 60) return `hace ${diffMins}m`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `hace ${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `hace ${diffDays}d`;
  return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
}

export default function UnifiedNotificationInbox() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({ total: 0, critical: 0 });
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);

  const token = localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const [inboxRes, countRes] = await Promise.all([
        fetch(`${API_URL}/api/unified-notifications/inbox?limit=30&include_read=false`, { headers }),
        fetch(`${API_URL}/api/unified-notifications/count`, { headers })
      ]);
      
      if (inboxRes.ok) {
        const data = await inboxRes.json();
        setNotifications(data.notifications || []);
        setUnreadCounts(data.unread_counts || { total: 0, critical: 0 });
      }
      if (countRes.ok) {
        const countData = await countRes.json();
        setUnreadCounts(prev => ({ ...prev, total: countData.unread, critical: countData.critical }));
      }
    } catch (e) {
      console.error('Error fetching notifications:', e);
    }
  }, [token]);

  // Poll every 30 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkRead = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/unified-notifications/mark-read/${notificationId}`, {
        method: 'POST', headers
      });
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      setUnreadCounts(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
    } catch (e) { /* ignore */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await fetch(`${API_URL}/api/unified-notifications/mark-all-read`, {
        method: 'POST', headers
      });
      setNotifications([]);
      setUnreadCounts({ total: 0, critical: 0, high: 0, medium: 0, low: 0 });
    } catch (e) { /* ignore */ }
  };

  const handleDismiss = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/unified-notifications/dismiss/${notificationId}`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      setUnreadCounts(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
    } catch (e) { /* ignore */ }
  };

  const handleDelete = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/unified-notifications/delete/${notificationId}`, {
        method: 'DELETE', headers
      });
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      setHistory(prev => prev.filter(n => n.notification_id !== notificationId));
      setUnreadCounts(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
    } catch (e) { console.error('Delete failed:', e); }
  };

  const handleDeleteAll = async () => {
    try {
      await fetch(`${API_URL}/api/unified-notifications/delete-all`, {
        method: 'DELETE', headers
      });
      setNotifications([]);
      setHistory([]);
      setUnreadCounts({ total: 0, critical: 0, high: 0, medium: 0, low: 0 });
    } catch (e) { console.error('Delete all failed:', e); }
  };

  const handleAction = (notif) => {
    if (notif.action_url) {
      navigate(notif.action_url);
      setIsOpen(false);
    }
    handleMarkRead(notif.notification_id);
  };

  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/unified-notifications/history?limit=50`, { headers });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.notifications || []);
      }
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  const totalUnread = unreadCounts.total || 0;
  const hasCritical = (unreadCounts.critical || 0) > 0;

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={() => { setIsOpen(!isOpen); if (!isOpen) fetchNotifications(); }}
        className="relative p-2 rounded-xl hover:bg-gray-100 transition-colors"
      >
        <Bell className={`w-5 h-5 ${hasCritical ? 'text-red-500' : 'text-gray-600'}`} />
        {totalUnread > 0 && (
          <span className={`absolute -top-1 -right-1 w-5 h-5 ${hasCritical ? 'bg-red-500 animate-pulse' : 'bg-orange-500'} text-white text-[10px] font-bold rounded-full flex items-center justify-center`}>
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-12 w-96 max-h-[80vh] bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-gray-700" />
                <h3 className="font-semibold text-gray-900">Notificaciones</h3>
                {totalUnread > 0 && (
                  <span className="bg-orange-100 text-orange-700 text-xs font-bold px-2 py-0.5 rounded-full">
                    {totalUnread}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadHistory(); }}
                  className="p-1.5 hover:bg-gray-200 rounded-lg text-gray-500 text-xs"
                  title="Historial"
                >
                  <Archive className="w-4 h-4" />
                </button>
                {totalUnread > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="p-1.5 hover:bg-gray-200 rounded-lg text-gray-500 text-xs"
                    title="Marcar todas como leídas"
                  >
                    <CheckCheck className="w-4 h-4" />
                  </button>
                )}
                {(notifications.length > 0 || history.length > 0) && (
                  <button
                    onClick={handleDeleteAll}
                    className="p-1.5 hover:bg-red-100 rounded-lg text-red-500 text-xs"
                    title="Eliminar todas"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={fetchNotifications}
                  className="p-1.5 hover:bg-gray-200 rounded-lg text-gray-500"
                  title="Actualizar"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto max-h-[60vh]">
              {!showHistory ? (
                /* Active Notifications */
                notifications.length > 0 ? (
                  <div className="divide-y divide-gray-50">
                    {notifications.map(notif => {
                      const config = PRIORITY_CONFIG[notif.priority] || PRIORITY_CONFIG.low;
                      const Icon = config.icon;
                      return (
                        <div
                          key={notif.notification_id}
                          onClick={() => notif.action_url && handleAction(notif)}
                          className={`px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer ${!notif.read ? 'bg-white' : 'bg-gray-50/50'} ${notif.action_url ? 'hover:bg-orange-50' : ''}`}
                        >
                          <div className="flex gap-3">
                            <div className={`w-8 h-8 rounded-full ${config.color} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                              <Icon className="w-4 h-4 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-sm font-medium text-gray-900 leading-tight">{notif.title}</p>
                                <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0">
                                  {timeAgo(notif.created_at)}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notif.message}</p>
                              
                              <div className="flex items-center gap-2 mt-2">
                                {notif.action_url && (
                                  <span className="text-xs font-medium text-orange-600 flex items-center gap-1">
                                    Ver detalle <ChevronRight className="w-3 h-3" />
                                  </span>
                                )}
                                <div className="flex-1" />
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleMarkRead(notif.notification_id); }}
                                  className="p-1 text-gray-400 hover:text-green-500 rounded"
                                  title="Marcar leída"
                                >
                                  <Check className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDelete(notif.notification_id); }}
                                  className="p-1 text-gray-400 hover:text-red-500 rounded"
                                  title="Eliminar"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="py-12 text-center">
                    <Bell className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">Sin notificaciones nuevas</p>
                    <p className="text-xs text-gray-400 mt-1">¡Todo al día!</p>
                  </div>
                )
              ) : (
                /* History */
                history.length > 0 ? (
                  <div className="divide-y divide-gray-50">
                    <div className="px-4 py-2 bg-gray-100">
                      <p className="text-xs font-medium text-gray-500">Historial de notificaciones</p>
                    </div>
                    {history.map(notif => {
                      const config = PRIORITY_CONFIG[notif.priority] || PRIORITY_CONFIG.low;
                      return (
                        <div key={notif.notification_id} className="px-4 py-2.5 opacity-60">
                          <div className="flex gap-2 items-start">
                            <div className={`w-2 h-2 rounded-full ${config.color} mt-1.5 flex-shrink-0`} />
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-gray-700 truncate">{notif.title}</p>
                              <p className="text-[10px] text-gray-400">{timeAgo(notif.created_at)}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <p className="text-sm text-gray-400">Sin historial</p>
                  </div>
                )
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
